import React, { useEffect, useState } from "react";
import { listAdmins, addAdmin, removeAdmin, formatApiErrorDetail } from "../../lib/auth";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { ShieldCheck, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function TeamAccessCard() {
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAdmins = async () => {
    try {
      setAdmins(await listAdmins());
    } catch (e) {
      console.error("Failed to load admin list", e);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleAdd = async () => {
    const email = newAdmin.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await addAdmin(email);
      setNewAdmin("");
      await loadAdmins();
      toast.success(`${email} can now access the admin dashboard.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add admin.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (email) => {
    setBusy(true);
    try {
      await removeAdmin(email);
      await loadAdmins();
      toast.success(`Removed ${email}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove admin.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-8" data-testid="team-access-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <p className="font-display font-600 text-foreground">Team access</p>
          <span className="text-xs text-muted-foreground">Owner only — approve who can sign in.</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Input
            data-testid="new-admin-input"
            type="email"
            placeholder="colleague@organization.ca"
            value={newAdmin}
            onChange={(e) => setNewAdmin(e.target.value)}
            className="flex-1"
          />
          <Button onClick={handleAdd} disabled={busy || !newAdmin.trim()} data-testid="add-admin-btn" className="gap-2">
            <UserPlus className="h-4 w-4" /> Approve email
          </Button>
        </div>
        <div className="divide-y divide-border rounded-lg border border-border" data-testid="admins-list">
          {admins.map((a) => (
            <div key={a.email} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{a.email}</span>
                <Badge variant={a.role === "owner" ? "default" : "secondary"} className="text-xs capitalize">
                  {a.role}
                </Badge>
              </div>
              {a.role !== "owner" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(a.email)}
                  disabled={busy}
                  data-testid={`remove-admin-${a.email}`}
                  className="text-destructive hover:text-destructive gap-1.5"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
